import * as React from 'react';

import type {BillingRoute} from 'shells/billing/types';
import {NumberedBreadcrumbs} from 'shells/numbered-breadcrumbs/numbered-breadcrumbs';
import type {Breadcrumb} from 'shells/numbered-breadcrumbs/numbered-breadcrumbs';

import {buildBreadcrumbs, navigateToRoute} from './helpers';

export type ManageBreadcrumb = Breadcrumb & {
    id: BillingRoute;
};

type Props = {
    route: BillingRoute;
    planDetails: string | null | undefined;
    engagementDetails: string | null | undefined;
    marketingDetails: string | null | undefined;
    hasUnsavedBillingChanges: boolean;
};

export function ManageBreadcrumbs(props: Props) {
    const selectedBreadcrumbRef = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
        if (selectedBreadcrumbRef.current) {
            selectedBreadcrumbRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    }, [props.route]);

    const breadcrumbs: ManageBreadcrumb[] = [
        {
            id: 'sales',
            title: 'Sales plan',
            subtitle: props.planDetails,
            selectable: true,
        },
        {
            id: 'marketing',
            title: 'Marketing plan',
            subtitle: props.marketingDetails,
            selectable:
                props.route === 'engagement' ||
                props.route === 'add-ons' ||
                props.route === 'review',
        },
        {
            id: 'engagement',
            title: 'Engagement plan',
            subtitle: props.engagementDetails,
            selectable: props.route === 'add-ons' || props.route === 'review',
        },
        {
            id: 'add-ons',
            title: 'Add-ons',
            selectable: props.route === 'review',
        },
        {
            id: 'review',
            title: 'Review & purchase',
            selectable: false,
        },
    ];

    const handleSelectValue = (route: string) => {
        if (
            route === 'sales' ||
            route === 'marketing' ||
            route === 'engagement' ||
            route === 'add-ons' ||
            route === 'review'
        ) {
            navigateToRoute(route);
        }
    };

    return (
        <NumberedBreadcrumbs
            breadcrumbs={buildBreadcrumbs(breadcrumbs)}
            selectedValue={props.route}
            onSelectValue={handleSelectValue}
            onlyDisplaySubtitleWhenCompleted={true}
            subtitleSize='mini'
            subtitleColor='light'
            selectedItemRef={selectedBreadcrumbRef}
            hasUnsavedBillingChanges={props.hasUnsavedBillingChanges}
        />
    );
}
